/**
 * VAPID Web Push Notifications
 *
 * Primary push notification system using standard Web Push API with VAPID keys.
 * Works on iOS Safari 16.4+, Chrome, Firefox, Edge, and all modern browsers.
 *
 * The backend serves the VAPID public key at GET /api/web-push/vapid-public-key
 * and manages subscriptions at POST /api/web-push/subscribe.
 */

import { webPushAPI } from '../services/api';

// Cached VAPID key to avoid repeated fetches
let cachedVapidKey: string | null = null;

/**
 * Check if push permission can be requested.
 * Requires: Notification API + service worker + PushManager
 */
export function canRequestPushPermission(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Get current push permission state
 */
export function getPushPermissionState(): 'default' | 'granted' | 'denied' {
  if (!canRequestPushPermission()) {
    return 'default';
  }
  return Notification.permission;
}

/**
 * Fetch VAPID public key from backend (cached after first fetch)
 */
async function getVapidPublicKey(): Promise<string | null> {
  if (cachedVapidKey) return cachedVapidKey;

  try {
    const response = await webPushAPI.getVapidPublicKey();
    const key = response.data?.publicKey;
    if (key) {
      cachedVapidKey = key;
      return key;
    }
  } catch (error) {
    console.error('[WebPush] Failed to fetch VAPID public key:', error);
    // intentional: caller checks for null and returns descriptive error to user
  }
  return null;
}

/**
 * Convert a base64 URL-safe string to a Uint8Array (required by PushManager.subscribe)
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Set up VAPID web push with backend integration.
 * 1. Request notification permission
 * 2. Subscribe via PushManager with VAPID key
 * 3. Send subscription to backend
 */
export async function setupIOSPushWithBackend(_apiUrl: string): Promise<{
  success: boolean;
  error?: string;
}> {
  if (!canRequestPushPermission()) {
    return { success: false, error: 'Push notifications are not supported on this device' };
  }

  try {
    // 1. Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Notification permission was denied' };
    }

    // 2. Get VAPID public key
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) {
      return { success: false, error: 'Push notification service is not configured' };
    }

    // 3. Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // 4. Subscribe via PushManager
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast to BufferSource rather than typing urlBase64ToUint8Array's return as a
      // parameterized Uint8Array<ArrayBuffer> — this repo's resolved @types/node
      // version differs between the workspace root and web/node_modules (20.19.43 vs
      // 20.19.42), and only one of those makes Uint8Array generic. A version-specific
      // generic annotation broke under whichever one didn't match; this cast is valid
      // under both.
      applicationServerKey: urlBase64ToUint8Array(vapidKey) as BufferSource,
    });

    // 5. Extract keys and send to backend
    const subscriptionJson = subscription.toJSON();
    await webPushAPI.subscribe({
      endpoint: subscriptionJson.endpoint!,
      keys: {
        p256dh: subscriptionJson.keys!.p256dh!,
        auth: subscriptionJson.keys!.auth!,
      },
    });

    // 6. Store subscription state locally
    localStorage.setItem('webPushSubscribed', 'true');
    localStorage.setItem('webPushEndpoint', subscriptionJson.endpoint!);

    return { success: true };
  } catch (error: any) {
    console.error('[WebPush] Setup failed:', error);
    // intentional: error.message is surfaced to caller which displays it to the user
    return {
      success: false,
      error: error.message || 'Failed to set up push notifications',
    };
  }
}

/**
 * Check if user is currently subscribed to push notifications
 */
export async function isSubscribedToPush(): Promise<boolean> {
  if (!canRequestPushPermission()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
}

/**
 * Unsubscribe from push notifications.
 *
 * authTokenOverride: explicit Bearer token to authenticate the backend call
 * with. The awaits below (serviceWorker.ready, getSubscription,
 * subscription.unsubscribe) mean the actual network request fires several
 * ticks after the caller's synchronous continuation -- long enough for a
 * caller like logout() to have already cleared localStorage's authToken, or
 * switchToAccount() to have already swapped it to a different user. Passing
 * the outgoing user's token explicitly makes this correct regardless of that
 * timing (2026-08-15 Notifications red-team fix, CRITICAL).
 */
export async function unsubscribeFromPush(authTokenOverride?: string): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      localStorage.removeItem('webPushSubscribed');
      localStorage.removeItem('webPushEndpoint');
      return true;
    }

    // Unsubscribe from browser
    const success = await subscription.unsubscribe();
    if (!success) return false;

    // Tell backend to remove subscription
    try {
      await webPushAPI.unsubscribe(subscription.endpoint, authTokenOverride);
    } catch {
      // Non-fatal — subscription is already removed from browser
    }

    localStorage.removeItem('webPushSubscribed');
    localStorage.removeItem('webPushEndpoint');
    return true;
  } catch (error) {
    console.error('[WebPush] Unsubscribe failed:', error);
    // intentional: background retry, caller receives false and can surface error if needed
    return false;
  }
}

/**
 * Send a test push notification via the backend
 */
export async function sendTestPushNotification(_apiUrl: string): Promise<boolean> {
  try {
    await webPushAPI.sendTest();
    return true;
  } catch (error) {
    console.error('[WebPush] Test notification failed:', error);
    // intentional: caller receives false and can surface error if needed
    return false;
  }
}
